import React, { useState } from 'react';
import { NavLink, useNavigate, Outlet } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { authApi } from '@/lib/api';
import {
  LayoutDashboard, Monitor, Users, Shield, AlertTriangle, Camera,
  Video, BarChart3, GitBranch, Bot, ScrollText, Radio, KeyRound,
  LogOut, Menu, X, Eye, EyeOff, UserCog, ShieldAlert, FileText, Globe
} from 'lucide-react';

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import ErrorBoundary from '@/components/ErrorBoundary';
import Logo from '@/components/Logo';

const navItems = [
  { to: '/', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/devices', icon: Monitor, label: 'Devices' },
  { to: '/employees', icon: Users, label: 'Employees' },
  { to: '/policies', icon: Shield, label: 'Policies' },
  { to: '/alerts', icon: AlertTriangle, label: 'Alerts' },
  { type: 'divider', label: 'MEDIA' },
  { to: '/screenshots', icon: Camera, label: 'Screenshots' },
  { to: '/recordings', icon: Video, label: 'Recordings' },
  { type: 'divider', label: 'ANALYTICS' },
  { to: '/analytics/productivity', icon: BarChart3, label: 'Productivity' },
  { to: '/analytics/productivity#web', icon: Globe, label: 'Web Activity' },
  { to: '/analytics/git', icon: GitBranch, label: 'Git Activity' },
  { to: '/analytics/ai', icon: Bot, label: 'AI Usage' },

  { to: '/analytics/risk', icon: ShieldAlert, label: 'Risk Scores' },
  { to: '/reports', icon: FileText, label: 'Reports' },
  { type: 'divider', label: 'SYSTEM' },
  { to: '/remote-sessions', icon: Radio, label: 'Remote Sessions' },
  { to: '/audit-logs', icon: ScrollText, label: 'Audit Logs' },
  { to: '/enrollment', icon: KeyRound, label: 'Enrollment' },
  { to: '/teams', icon: Users, label: 'Departments' },
  { to: '/users', icon: UserCog, label: 'Admin Users' },
];

function ForceChangePasswordModal({ onSuccess }) {
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPw !== confirmPw) { setError("Passwords don't match"); return; }
    if (newPw.length < 8) { setError("New password must be at least 8 characters"); return; }
    setSaving(true);
    setError('');
    try {
      await authApi.changePassword(currentPw, newPw);
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open>
      <DialogContent
        className="bg-[#0B1220] border-amber-500/40 text-white"
        onInteractOutside={e => e.preventDefault()}
        onEscapeKeyDown={e => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="text-amber-400 flex items-center gap-2">
            <Shield className="w-5 h-5" /> Password Change Required
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-400 mt-1">
          Your password must be changed before you can access the dashboard.
        </p>
        <form onSubmit={handleSubmit} className="space-y-4 mt-3">
          {error && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-sm text-red-400">{error}</div>
          )}
          <div>
            <label className="text-xs text-slate-400 block mb-1">Current (Temporary) Password</label>
            <div className="relative">
              <input type={showCurrent ? 'text' : 'password'} required value={currentPw}
                onChange={e => setCurrentPw(e.target.value)}
                className="w-full px-3 py-2.5 bg-[#101A2E] border border-[#1C2A44] rounded-lg text-sm text-white pr-10 focus:ring-1 focus:ring-amber-500 outline-none"
                data-testid="force-change-current-pw" />
              <button type="button" onClick={() => setShowCurrent(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300">
                {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-400 block mb-1">New Password <span className="text-slate-600">(min 8 chars)</span></label>
            <div className="relative">
              <input type={showNew ? 'text' : 'password'} required minLength={8} value={newPw}
                onChange={e => setNewPw(e.target.value)}
                className="w-full px-3 py-2.5 bg-[#101A2E] border border-[#1C2A44] rounded-lg text-sm text-white pr-10 focus:ring-1 focus:ring-amber-500 outline-none"
                data-testid="force-change-new-pw" />
              <button type="button" onClick={() => setShowNew(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300">
                {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-400 block mb-1">Confirm New Password</label>
            <input type="password" required minLength={8} value={confirmPw}
              onChange={e => setConfirmPw(e.target.value)}
              className="w-full px-3 py-2.5 bg-[#101A2E] border border-[#1C2A44] rounded-lg text-sm text-white focus:ring-1 focus:ring-amber-500 outline-none"
              data-testid="force-change-confirm-pw" />
          </div>
          <button type="submit" disabled={saving}
            className="w-full bg-amber-500 text-white font-bold py-2.5 rounded-lg hover:brightness-110 disabled:opacity-50 transition-all"
            data-testid="force-change-submit-btn">
            {saving ? 'Updating Password...' : 'Set New Password & Continue'}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Layout() {
  const { user, logout, updateUser } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="flex h-screen w-full overflow-hidden bg-[#050A10]">
      {/* Force Password Change Gate */}
      {user?.force_password_change && (
        <ForceChangePasswordModal onSuccess={() => updateUser({ ...user, force_password_change: false })} />
      )}

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/60 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50 w-64 flex-shrink-0
        border-r border-slate-800 bg-slate-900/95 flex flex-col
        transform transition-transform duration-200
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="p-5 flex flex-col h-full">
          <div className="mb-6 border-b border-slate-700/50 pb-4">
            <div className="flex items-start justify-between">
              <div data-testid="app-logo">
                <Logo size="md" />
              </div>
              <button className="lg:hidden mt-1 text-slate-500 hover:text-white" onClick={() => setSidebarOpen(false)}>
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="mt-3 max-w-[12rem] text-[11px] uppercase tracking-[0.24em] text-slate-500">
              Enterprise workforce risk visibility
            </p>
          </div>

          {/* Nav */}
          <nav className="flex-1 space-y-0.5 overflow-y-auto" data-testid="sidebar-nav">
            {navItems.map((item, i) => {
              if (item.type === 'divider') {
                return (
                  <div key={i} className="pt-4 pb-2">
                    <span className="px-3 text-[10px] font-bold uppercase tracking-[0.28em] text-slate-600">{item.label}</span>
                  </div>
                );
              }
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/'}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all duration-150 ${
                      isActive
                        ? 'border border-blue-500/20 bg-gradient-to-r from-blue-500/15 to-purple-500/10 text-slate-100 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]'
                        : 'border border-transparent text-slate-400 hover:border-slate-700/60 hover:bg-slate-800/70 hover:text-slate-100'
                    }`
                  }
                  onClick={() => setSidebarOpen(false)}
                >
                  <Icon className="w-4 h-4 flex-shrink-0" />
                  <span>{item.label}</span>
                </NavLink>
              );
            })}
          </nav>

          {/* User section */}
          <div className="mt-auto pt-4 border-t border-slate-800">
            <div className="rounded-xl border border-slate-800 bg-slate-800/70 p-3 mb-3">
              <p className="text-xs font-semibold text-slate-300">System Health</p>
              <div className="mt-2 h-1.5 w-full rounded-full bg-slate-700 overflow-hidden">
                <div className="h-full w-4/5 rounded-full bg-gradient-to-r from-blue-500 to-purple-500" />
              </div>
              <p className="mt-1.5 text-[10px] uppercase tracking-[0.22em] text-slate-500">Status: Operational</p>
            </div>
            <div className="flex items-center gap-3 px-2">
              <div className="w-8 h-8 rounded-full border border-blue-500/20 bg-blue-500/10 flex items-center justify-center text-blue-300 text-xs font-bold">
                {user?.email?.[0]?.toUpperCase() || 'A'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-slate-200 truncate">{user?.email}</p>
                <p className="text-[10px] text-slate-500 uppercase tracking-[0.18em]">{user?.role?.replace('_', ' ')}</p>
              </div>
              <button onClick={handleLogout} className="text-slate-500 hover:text-red-400 transition-colors" data-testid="logout-btn">
                <LogOut className="w-4 h-4" />
              </button>
            </div>
            <div className="mt-4 border-t border-slate-700/50 px-2 pt-4">
              <p className="text-xs text-slate-600">v1.0.0</p>
              <p className="mt-1 text-[10px] uppercase tracking-wider text-slate-500">Adaptive Monitoring &amp; Response</p>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 flex flex-col overflow-hidden">
        {/* Topbar */}
        <header className="h-14 border-b border-slate-800 bg-slate-900/80 backdrop-blur-sm flex items-center px-4 lg:px-6 flex-shrink-0">
          <button className="lg:hidden mr-3 text-slate-400 hover:text-white" onClick={() => setSidebarOpen(true)} data-testid="mobile-menu-btn">
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex-1" />
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-slate-500">
              <span className="flex h-2 w-2 rounded-full bg-blue-400 animate-pulse" />
              <span>LIVE</span>
            </div>
          </div>
        </header>

        {/* Page content */}
        <div className="flex-1 overflow-auto p-4 lg:p-6">
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </div>
      </main>
    </div>
  );
}
